/**
 * Stacking order for the horizontal schedule timelines.
 *
 * The layers live in one place because they are read across files: an
 * assignment block, the important-artist glow, the sticky label column and the
 * current-time marker are rendered by different components but paint into the
 * same stacking context.
 *
 * Contract in both views, low → high:
 *   block < glow < marker line < sticky label column < marker time label
 *
 * Two rules the marker must obey:
 *  - it clears the blocks, otherwise "now" hides behind whatever is scheduled
 *    at that moment (the read-only timeline used to draw it underneath);
 *  - it stays under the sticky label column, so it can't paint over the stage
 *    and custom-subject names while the timeline is scrolled.
 *
 * The creator and the read-only timeline grew separate numeric scales; they are
 * kept apart here (rather than unified) because each view's own elements —
 * sticky header, resize handles, feedback pins — are woven through those
 * numbers. What matters is that the ordering above holds within each scale.
 *
 * Values are literal Tailwind classes (not computed) so the JIT compiler picks
 * them up.
 */
const layer = (z: number, className: string) => ({ z, className }) as const;

/** Schedule creator (`CrewSchedule`). */
export const CREATOR_LAYERS = {
  /** Crew assignment block on a stage / custom row. */
  block: layer(10, "z-10"),
  /** The vertical current-time line (shares its level with feedback pins). */
  currentTimeLine: layer(20, "z-20"),
  /** Sticky stage / crew label column on the left. */
  stickyLabel: layer(30, "z-30"),
  /** Sticky hour header on top. */
  header: layer(40, "z-40"),
  /** The current-time pill above the hour header. */
  currentTimeLabel: layer(50, "z-50"),
} as const;

/** Read-only / client-share timeline (`ReadOnlyScheduleTimeline`). */
export const READONLY_LAYERS = {
  /** Crew assignment block on a stage row. */
  block: layer(30, "z-30"),
  /** "Important artist" glow overlay framing a block. */
  glow: layer(40, "z-40"),
  /** The vertical current-time line spanning the row. */
  currentTimeLine: layer(44, "z-[44]"),
  /** Sticky stage / crew label column on the left. */
  stickyLabel: layer(45, "z-[45]"),
  /** The current-time pill above the hour header. */
  currentTimeLabel: layer(50, "z-50"),
} as const;
